// Search sub-panel for AddAssetDialog — renders the input, search
// state, result groups, and the "Create variant" CTA. Split from
// the dialog so the max-lines cap holds for both files. Dialog
// owns all state; this module is purely presentational.
//
// Visual contract (field-design redesign pack): rounded search
// field, system display headers, sub-system labels as tracking-
// uppercase lines with hairline divider, compact result rows with
// chevron, and a "Can't find it? Create variant" pill footer.
//
// Result-list visuals (system/sub-system headers + chevron rows)
// live in `./add-asset-search-result-groups` to keep this file
// under the project's max-lines cap. Shape types live in
// `./add-asset-search-results-types` to avoid a circular import.

import type { ReactElement } from 'react'

import {
  ASSET_TYPE_SEARCH_MIN_QUERY,
  type AssetTypeSearchRow,
} from '../../hooks/use-classification'
import { Alert } from '../../ui/alert'
import { Spinner } from '../../ui/spinner'

import { ResultGroups } from './add-asset-search-result-groups'
import type {
  SubSystemGroup,
  SystemGroup,
} from './add-asset-search-results-types'

// Dispatch failures (`createItem` + variant-create hand-off) are
// surfaced by the parent dialog so one alert spans every mode.
// This component only renders the search-failure alert.

export interface SearchState {
  status: 'idle' | 'loading' | 'ready' | 'error'
  query: string
  results: AssetTypeSearchRow[]
  errorMessage: string | null
}

export interface AddAssetSearchResultsProps {
  inputValue: string
  onInputChange: (next: string) => void
  searchState: SearchState
  visibleResults: AssetTypeSearchRow[]
  excludedCount: number
  // Token-prefix matches grouped by System → SubSystem; primary list.
  primaryGroups: SystemGroup[]
  // Substring-only fallback grouped by System → SubSystem; shown
  // under a "Did you mean…?" header only when `primaryGroups` is
  // empty (PRD §6.5 fallback before "Create variant").
  secondaryGroups: SystemGroup[]
  // Rows visible — drives the "N results" count. Equals primary
  // count if non-empty, else secondary count.
  visibleCount: number
  creatingAssetTypeId: string | null
  canCreateVariant: boolean
  onSelectExisting: (row: AssetTypeSearchRow) => void
  // Fired by both create-variant entry points (empty-state CTA and
  // result-row footer pill). Hands the typed query to the dialog so
  // the SubSystemPicker can pre-fill the variant form's name. Both
  // flows share the same downstream shape — the user explicitly
  // picks the parent sub-system before the form opens (PRD §6.5).
  onCreateFromQuery: (name: string) => void
}

export type { SubSystemGroup, SystemGroup }

export function groupBySystem(rows: AssetTypeSearchRow[]): SystemGroup[] {
  const systems: SystemGroup[] = []
  const systemIndex = new Map<string, SystemGroup>()
  const subIndex = new Map<string, SubSystemGroup>()
  for (const row of rows) {
    let systemGroup = systemIndex.get(row.systemId)
    if (systemGroup === undefined) {
      systemGroup = {
        systemId: row.systemId,
        systemName: row.systemName,
        subSystems: [],
      }
      systemIndex.set(row.systemId, systemGroup)
      systems.push(systemGroup)
    }
    const subKey = `${row.systemId}::${row.subSystemId}`
    let subGroup = subIndex.get(subKey)
    if (subGroup === undefined) {
      subGroup = {
        subSystemId: row.subSystemId,
        subSystemName: row.subSystemName,
        types: [],
      }
      subIndex.set(subKey, subGroup)
      systemGroup.subSystems.push(subGroup)
    }
    subGroup.types.push(row)
  }
  return systems
}

export function AddAssetSearchResults({
  inputValue,
  onInputChange,
  searchState,
  visibleResults,
  excludedCount,
  primaryGroups,
  secondaryGroups,
  visibleCount,
  creatingAssetTypeId,
  canCreateVariant,
  onSelectExisting,
  onCreateFromQuery,
}: AddAssetSearchResultsProps): ReactElement {
  const trimmed = inputValue.trim()
  const belowMinQuery =
    searchState.status === 'idle' &&
    trimmed.length > 0 &&
    trimmed.length < ASSET_TYPE_SEARCH_MIN_QUERY
  const emptyPrompt = searchState.status === 'idle' && trimmed.length === 0
  const zeroResults =
    searchState.status === 'ready' && visibleResults.length === 0
  const showZeroResultsCreateCta =
    zeroResults && canCreateVariant && searchState.query.length > 0
  const showSecondaryFallback =
    primaryGroups.length === 0 && secondaryGroups.length > 0
  const groupsToRender = showSecondaryFallback ? secondaryGroups : primaryGroups

  // The "Can't find it? Create variant" footer pill routes through
  // the SubSystemPicker (same flow as the empty-state CTA) — the
  // search-result row's classification is often wrong for the
  // variant the surveyor wants (PRD §6.5). The pill is disabled
  // during `loading`/`error` so the user sees up-to-date results.
  const showVariantFooter = canCreateVariant && visibleResults.length > 0
  const variantFooterDisabled =
    searchState.status === 'loading' || searchState.status === 'error'

  return (
    <>
      <SearchHeader
        inputValue={inputValue}
        onInputChange={onInputChange}
        searchState={searchState}
        totalCount={visibleCount}
      />

      <div
        className="min-h-0 flex-1 overflow-y-auto px-6 pb-3"
        role="region"
        aria-live="polite"
      >
        {emptyPrompt ? (
          <p className="px-1 py-3 text-sm text-cc-forest/60">
            Start typing to find an asset type.
          </p>
        ) : null}
        {belowMinQuery ? (
          <p className="px-1 py-3 text-sm text-cc-forest/60">
            Enter at least {ASSET_TYPE_SEARCH_MIN_QUERY} characters to search.
          </p>
        ) : null}
        {searchState.status === 'loading' ? (
          <div className="flex items-center gap-2 px-1 py-3 text-sm text-cc-forest/60">
            <Spinner label="Searching" size="sm" /> Searching…
          </div>
        ) : null}
        {searchState.status === 'error' ? (
          <div className="px-1 py-3">
            <Alert variant="error" title="Search failed">
              {searchState.errorMessage ?? 'Unexpected error'}
            </Alert>
          </div>
        ) : null}
        {zeroResults ? (
          <ZeroResultsState
            query={searchState.query}
            excludedCount={excludedCount}
            showCreateCta={showZeroResultsCreateCta}
            onCreateFromQuery={onCreateFromQuery}
          />
        ) : null}
        {showSecondaryFallback ? (
          <p className="px-1 pt-3 pb-1 text-xs text-cc-forest/55">
            No matches — did you mean&hellip;?
          </p>
        ) : null}
        {groupsToRender.length > 0 ? (
          <ResultGroups
            groups={groupsToRender}
            creatingAssetTypeId={creatingAssetTypeId}
            onSelectExisting={onSelectExisting}
          />
        ) : null}
      </div>

      {showVariantFooter ? (
        <footer className="flex items-center justify-end gap-4 border-t border-cc-forest/10 bg-cc-cream/50 px-6 py-4">
          <button
            type="button"
            onClick={() => onCreateFromQuery(searchState.query)}
            disabled={variantFooterDisabled}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-pill bg-cc-forest px-4 py-2 text-[13px] font-medium text-cc-cream hover:bg-cc-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cc-lime disabled:cursor-not-allowed disabled:opacity-50"
          >
            Can&rsquo;t find it? Create variant
            <svg
              aria-hidden="true"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M7 2 V12 M2 7 H12"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </footer>
      ) : null}
    </>
  )
}

interface SearchHeaderProps {
  inputValue: string
  onInputChange: (next: string) => void
  searchState: SearchState
  totalCount: number
}

function SearchHeader({
  inputValue,
  onInputChange,
  searchState,
  totalCount,
}: SearchHeaderProps): ReactElement {
  const showCount =
    searchState.status === 'ready' || searchState.status === 'loading'
  return (
    <div className="bg-white px-6 pt-4 pb-3">
      <div className="group relative">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-cc-forest/40 transition-colors group-focus-within:text-cc-forest"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.6" />
            <path
              d="M11 11 L14 14"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <label htmlFor="add-asset-search" className="sr-only">
          Search asset types
        </label>
        <input
          id="add-asset-search"
          type="search"
          value={inputValue}
          onChange={(e) => onInputChange(e.currentTarget.value)}
          aria-label="Search asset types"
          autoComplete="off"
          placeholder="Search asset types…"
          // Suppress the user-agent clear-search glyph so the only X
          // visible is our themed `Clear search` button below — the
          // browser's grey native control was rendering side-by-side
          // with our cc-forest button at the input's right edge.
          // Targets WebKit/Chromium and the Edge-legacy MS variant.
          className="block min-h-11 w-full rounded-xl border border-transparent bg-cc-cream py-3 pl-10 pr-10 text-sm text-cc-forest placeholder:text-cc-forest/40 focus:border-cc-forest focus:bg-white focus:outline-none [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-ms-clear]:hidden"
        />
        {inputValue.length > 0 ? (
          <button
            type="button"
            onClick={() => onInputChange('')}
            aria-label="Clear search"
            className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-cc-forest/50 hover:bg-cc-forest/10 hover:text-cc-forest"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M1 1 L9 9 M9 1 L1 9"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : null}
      </div>
      {showCount && totalCount > 0 ? (
        <div className="mt-2 px-1 text-[11px] tracking-wide text-cc-forest/50">
          {totalCount} {totalCount === 1 ? 'result' : 'results'}
        </div>
      ) : null}
    </div>
  )
}

interface ZeroResultsStateProps {
  query: string
  excludedCount: number
  showCreateCta: boolean
  onCreateFromQuery: (name: string) => void
}

function ZeroResultsState({
  query,
  excludedCount,
  showCreateCta,
  onCreateFromQuery,
}: ZeroResultsStateProps): ReactElement {
  return (
    <div className="space-y-3 px-1 py-6 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-cc-cream text-cc-forest/40">
        <svg
          aria-hidden="true"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M14 14 L18 18"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <p className="text-sm font-medium text-cc-forest">
        No matches for &ldquo;{query}&rdquo;
      </p>
      {excludedCount > 0 ? (
        <p className="text-xs text-cc-forest/55">
          ({excludedCount} already added to this room.)
        </p>
      ) : (
        <p className="text-xs text-cc-forest/55">
          Try a different search, or create a new variant.
        </p>
      )}
      {showCreateCta ? (
        <button
          type="button"
          onClick={() => onCreateFromQuery(query)}
          className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-pill bg-cc-lime px-4 py-2 text-[13px] font-medium text-cc-forest hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cc-forest"
        >
          <span aria-hidden="true">+</span> Create &ldquo;{query}&rdquo; as a new
          asset
        </button>
      ) : null}
    </div>
  )
}
